import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { customAlphabet } from 'nanoid'
import { useAuth } from '../contexts/AuthContext'
import UserInfo from '../components/UserInfo'
import logo from '../assets/logo.svg'

const nanoid = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 16)

const PhoneIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

function Home() {
  const { user, signInWithGoogle, sendEmailLink, needsEmailConfirm, completeEmailSignIn } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [emailError, setEmailError] = useState('')

  useEffect(() => {
    if (user) {
      const next = searchParams.get('next')
      if (next) {
        navigate(next)
      }
    }
  }, [user, navigate, searchParams])

  const createCall = () => {
    const roomId = nanoid()
    navigate(`/room/${roomId}`)
  }

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle()
    } catch (error) {
      console.error('Google sign-in error:', error)
      Sentry.captureException(error)
      alert('Failed to sign in with Google')
    }
  }

  const handleEmailLink = async (e) => {
    e.preventDefault()
    setEmailError('')
    try {
      await sendEmailLink(email)
      setEmailSent(true)
    } catch (error) {
      console.error('Email link error:', error)
      Sentry.captureException(error)
      setEmailError('Failed to send sign-in link. Please try again.')
    }
  }

  const handleEmailConfirm = async (e) => {
    e.preventDefault()
    setEmailError('')
    try {
      await completeEmailSignIn(email)
    } catch (error) {
      console.error('Email confirm error:', error)
      Sentry.captureException(error)
      setEmailError('Failed to sign in. Please try again.')
    }
  }

  return (
    <div className="home">
      {user && <UserInfo />}

      <img src={logo} alt="Call Mom" className="home-logo" />
      <h1>callmom.io</h1>
      <p>{user ? 'Your video call is one click away' : needsEmailConfirm ? 'Enter your email to complete sign-in' : 'Sign in to start video call'}</p>

      {needsEmailConfirm ? (
        <div className="login-buttons">
          <form className="email-link-form" onSubmit={handleEmailConfirm}>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit" className="social-btn email">
              Confirm & Sign In
            </button>
            {emailError && <p className="email-error">{emailError}</p>}
          </form>
        </div>
      ) : user ? (
        <button className="create-call-btn" onClick={createCall} title="Start a call">
          <PhoneIcon />
        </button>
      ) : (
        <div className="login-buttons">
          {emailSent ? (
            <p className="email-sent-msg">Check your email for a sign-in link!</p>
          ) : (
            <form className="email-link-form" onSubmit={handleEmailLink}>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="social-btn email">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                Sign in with Email
              </button>
              {emailError && <p className="email-error">{emailError}</p>}
            </form>
          )}
          <div className="login-divider">
            <span>or</span>
          </div>
          <button className="social-btn google" onClick={handleGoogleSignIn}>
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>
        </div>
      )}

      <ul className="features-list">
        <li>🔒 <b>Private by default</b> — end-to-end encrypted</li>
        <li>⚡ <b>Faster calls</b> — no server hops</li>
        <li>🌍 <b>Works anywhere</b> — just a link and a browser</li>
      </ul>

      <footer className="home-footer">
        <p>&copy; {new Date().getFullYear()} callmom.io</p>
        <Link to="/privacy">Privacy Policy</Link>
      </footer>
    </div>
  )
}

export default Home
